import { useState, useEffect } from 'react';
import { useConversation } from '@elevenlabs/react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Mic, Loader2, PhoneOff, Volume2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AIWaiterChatVoice({ tableId, restaurantId, onOrderComplete }) {
  const [isOpen, setIsOpen] = useState(false);
  const [conversationToken, setConversationToken] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationStatus, setConversationStatus] = useState('idle'); // idle, connecting, active, ended
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderSummary, setOrderSummary] = useState(null);
  const [conversationTranscript, setConversationTranscript] = useState([]);
  const [orderProcessed, setOrderProcessed] = useState(false);
  const [menuItems, setMenuItems] = useState([]);

  // Client tool function to place order
  const placeOrder = async ({ customerName, customerPhone, items, specialInstructions }) => {
    console.log('🎯 placeOrder FUNCTION CALLED!!!');
    console.log('🎯 Parameters:', { customerName, customerPhone, items, specialInstructions });
    
    try {
      console.log('📦 Placing order:', { customerName, customerPhone, items, specialInstructions });
      
      // Format items for backend
      const formattedItems = items.map(item => ({
        item_name: item.name,
        quantity: item.quantity,
        price: item.price,
        notes: item.notes || specialInstructions || ''
      }));

      // Calculate total
      const total = formattedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

      // Prepare order data
      const orderData = {
        table_id: tableId || null,
        order_type: tableId ? 'dine_in' : 'online',
        items: formattedItems,
        customer_name: customerName,
        customer_phone: customerPhone,
        grand_total: total,
        notes: specialInstructions || '',
        created_by: 'AI Waiter Voice'
      };

      // Send order to backend
      const response = await axios.post(`${API}/orders/customer`, orderData);

      if (response.status === 200 || response.status === 201) {
        // Store order summary for display
        setOrderSummary({
          orderId: response.data.id,
          customerName,
          customerPhone,
          items: formattedItems,
          total,
          specialInstructions
        });
        
        setOrderPlaced(true);
        
        // Disconnect the voice call
        setTimeout(async () => {
          try {
            await conversation.endSession();
            setConversationStatus('ended');
          } catch (err) {
            console.error('Error ending session:', err);
          }
        }, 1000); // Small delay to let AI finish speaking
        
        toast.success('Order placed successfully!');
        
        // Call parent callback if provided
        if (onOrderComplete) {
          onOrderComplete(response.data);
        }
        
        return {
          success: true,
          message: 'Order placed successfully! Your order will be prepared shortly.',
          order_id: response.data.id
        };
      } else {
        throw new Error('Failed to place order');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order. Please try again.');
      return {
        success: false,
        message: 'Sorry, there was an error placing your order. Please try again.'
      };
    }
  };

  // Parse conversation and automatically place order
  const parseAndPlaceOrder = async (messages) => {
    // Prevent duplicate order placement
    if (orderProcessed) {
      console.log('⚠️ Order already processed, skipping...');
      return;
    }

    console.log('🔍 Analyzing conversation for order placement...', messages.length, 'messages');
    
    if (messages.length < 3) {
      console.log('⏳ Conversation too short, waiting for more messages...');
      return;
    }
    
    // Build full conversation text
    const fullConversation = messages.map(m => `${m.role}: ${m.text}`).join('\n');
    console.log('📝 Full conversation excerpt:', fullConversation.substring(0, 500));
    
    // Check if order confirmation happened - look at last 3 AI messages
    const aiMessages = messages.filter(m => m.role === 'assistant');
    const lastAiMessages = aiMessages.slice(-3).map(m => m.text.toLowerCase()).join(' ');
    
    console.log('🤖 Last AI messages:', lastAiMessages);
    
    const confirmationKeywords = [
      'order has been placed',
      'placing your order',
      "i'll place",
      'i will place',
      'order placed',
      'confirming your order',
      'order confirmed',
      'confirm your order',
      'place the order',
      'submit your order',
      'your order is',
      'order will be prepared'
    ];
    
    const hasConfirmation = confirmationKeywords.some(keyword => {
      const found = lastAiMessages.includes(keyword);
      if (found) console.log(`✅ Found confirmation keyword: "${keyword}"`);
      return found;
    });
    
    if (!hasConfirmation) {
      console.log('⏳ No order confirmation detected yet');
      return;
    }
    
    console.log('✅ Order confirmation DETECTED! Extracting details...');
    
    // Extract order details from conversation
    const orderDetails = extractOrderFromConversation(messages);
    
    console.log('📦 Extracted order details:', JSON.stringify(orderDetails, null, 2));
    
    // Check if we have minimum required info
    if (!orderDetails.customerName) {
      console.error('❌ Missing customer name');
      toast.error('Could not detect customer name. Please try again.');
      return;
    }
    
    if (!orderDetails.customerPhone) {
      console.error('❌ Missing phone number');
      toast.error('Could not detect phone number. Please try again.');
      return;
    }
    
    if (orderDetails.items.length === 0) {
      console.error('❌ No items found');
      toast.error('Could not detect any items. Please try again.');
      return;
    }
    
    console.log('✅ All required details extracted! Placing order...');
    
    // Mark as processed to prevent duplicates
    setOrderProcessed(true);
    
    // Place order automatically using the same endpoint as QR orders
    try {
      await placeOrder({
        customerName: orderDetails.customerName,
        customerPhone: orderDetails.customerPhone,
        items: orderDetails.items,
        specialInstructions: orderDetails.specialInstructions || ''
      });
      
      console.log('✅✅✅ Order placed automatically!');
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('❌ Failed to place order:', error);
      toast.error('Failed to place order: ' + error.message);
      setOrderProcessed(false); // Allow retry
    }
  };

  // Extract order details from conversation messages
  const extractOrderFromConversation = (messages) => {
    let customerName = '';
    let customerPhone = '';
    let items = [];
    let specialInstructions = '';
    
    // Build full conversation text for easier parsing
    const fullText = messages.map(m => m.text).join(' ');
    const lowerFullText = fullText.toLowerCase();
    
    console.log('🔍 Extracting from conversation...');
    
    // Extract customer name - look in user messages first
    const userMessages = messages.filter(m => m.role === 'user');
    for (const msg of userMessages) {
      const text = msg.text;
      const lowerText = text.toLowerCase();
      
      if (!customerName) {
        // Try different name patterns
        const namePatterns = [
          /(?:my name is|name is|i'm|i am|this is)\s+([A-Za-z][A-Za-z\s]{2,30})(?:\.|,|and|phone|number|\d|$)/i,
          /^([A-Za-z][A-Za-z\s]{2,30})(?:\s+here|,|\s+\d)/i  // Just name at start
        ];
        
        for (const pattern of namePatterns) {
          const match = text.match(pattern);
          if (match) {
            customerName = match[1].trim();
            // Remove common words that might be captured
            customerName = customerName.replace(/\b(calling|speaking|here|phone|number)\b/gi, '').trim();
            if (customerName.length >= 2) {
              console.log('👤 Found name:', customerName);
              break;
            }
          }
        }
      }
      
      // Extract phone number
      if (!customerPhone) {
        const phonePatterns = [
          /(\d{3}[-.\s]?\d{3}[-.\s]?\d{4})/,  // 123-456-7890 or 123.456.7890
          /(\d{10})/,  // 1234567890
          /(\+?\d{1,3}[-.\s]?\d{3}[-.\s]?\d{3}[-.\s]?\d{4})/  // +1-234-567-8900
        ];
        
        for (const pattern of phonePatterns) {
          const match = text.match(pattern);
          if (match) {
            customerPhone = match[1].replace(/[-.\s]/g, '');
            console.log('📱 Found phone:', customerPhone);
            break;
          }
        }
      }
    }
    
    // Extract items - look through ALL messages for menu items
    console.log('🔍 Searching for menu items in conversation...');
    console.log('📋 Available menu items:', menuItems.length);
    
    for (const menuItem of menuItems) {
      const itemName = menuItem.item_name || menuItem.name;
      if (!itemName) continue;
      
      const lowerItemName = itemName.toLowerCase();
      
      // Check if this menu item is mentioned anywhere
      if (lowerFullText.includes(lowerItemName)) {
        console.log(`✅ Found menu item "${itemName}" in conversation`);
        
        // Try to find quantity - look for numbers near the item name
        let quantity = 1; // Default
        
        // Look in the context around the item mention
        const itemIndex = lowerFullText.indexOf(lowerItemName);
        const context = fullText.substring(Math.max(0, itemIndex - 50), itemIndex + 50);
        
        const quantityPatterns = [
          /(\d+)\s*(?:x\s*)?/i,  // "2x" or "2"
          /\b(one|two|three|four|five|six|seven|eight|nine|ten)\b/i
        ];
        
        for (const pattern of quantityPatterns) {
          const match = context.match(pattern);
          if (match) {
            if (match[1].match(/\d+/)) {
              quantity = parseInt(match[1]);
            } else {
              // Convert word to number
              const wordToNum = {one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10};
              quantity = wordToNum[match[1].toLowerCase()] || 1;
            }
            break;
          }
        }
        
        // Get price from menu
        const price = menuItem.pricing?.dine_in || menuItem.price || 8.99; // Default price
        
        items.push({
          name: itemName,
          quantity: quantity,
          price: price,
          notes: ''
        });
        console.log('🍽️ Added item:', quantity, 'x', itemName, '@', price);
      }
    }
    
    // Deduplicate items (in case same item mentioned multiple times)
    const uniqueItems = [];
    const itemMap = new Map();
    
    for (const item of items) {
      const key = item.name.toLowerCase();
      if (itemMap.has(key)) {
        itemMap.get(key).quantity += item.quantity;
      } else {
        itemMap.set(key, {...item});
      }
    }
    
    itemMap.forEach(item => uniqueItems.push(item));
    
    return {
      customerName,
      customerPhone,
      items: uniqueItems,
      specialInstructions
    };
  };

  // Initialize ElevenLabs conversation with client tools
  const conversation = useConversation({
    onConnect: () => {
      console.log('🎤 ElevenLabs conversation connected');
      setConversationStatus('active');
    },
    onDisconnect: () => {
      console.log('🎤 ElevenLabs conversation disconnected');
      setConversationStatus('ended');
    },
    onMessage: (message) => {
      console.log('🎤 RAW Message received:', JSON.stringify(message, null, 2));
      
      // ElevenLabs message structure can vary - handle different formats
      let messageText = '';
      let messageRole = 'user';
      
      if (typeof message === 'string') {
        messageText = message;
      } else if (message.message) {
        messageText = message.message;
      } else if (message.text) {
        messageText = message.text;
      } else if (message.content) {
        messageText = message.content;
      }
      
      // Determine role
      if (message.source === 'ai' || message.role === 'assistant' || message.type === 'agent') {
        messageRole = 'assistant';
      }
      
      console.log(`🎤 Processed: [${messageRole}] ${messageText}`);
      
      // Add message to transcript
      const newMessage = {
        role: messageRole,
        text: messageText,
        timestamp: new Date().toISOString()
      };
      
      setConversationTranscript(prev => {
        const updated = [...prev, newMessage];
        
        // Log full conversation so far
        console.log('📝 Conversation history:', updated.length, 'messages');
        
        // Check for order placement after each AI message
        if (messageRole === 'assistant') {
          parseAndPlaceOrder(updated);
        }
        
        return updated;
      });
    },
    onError: (error) => {
      console.error('🎤 ElevenLabs error:', error);
    },
    clientTools: {
      placeOrder: {
        description: 'Place customer order in the restaurant POS system',
        parameters: {
          type: 'object',
          properties: {
            customerName: {
              type: 'string',
              description: 'Customer full name'
            },
            customerPhone: {
              type: 'string',
              description: 'Customer phone number'
            },
            items: {
              type: 'array',
              description: 'List of ordered items',
              items: {
                type: 'object',
                properties: {
                  name: { type: 'string' },
                  quantity: { type: 'number' },
                  price: { type: 'number' },
                  notes: { type: 'string' }
                }
              }
            },
            specialInstructions: {
              type: 'string',
              description: 'Any special instructions for the order'
            }
          },
          required: ['customerName', 'customerPhone', 'items']
        },
        handler: placeOrder
      }
    }
  });
  
  console.log('✅ AIWaiterChatVoice component initialized with placeOrder client tool');

  // Fetch menu items on mount
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${API}/public/menu/items?table_id=${tableId}`);
        if (response.data && Array.isArray(response.data)) {
          setMenuItems(response.data);
          console.log('📋 Menu items loaded:', response.data.length);
        }
      } catch (error) {
        console.error('Error fetching menu:', error);
      }
    };
    
    if (tableId) {
      fetchMenu();
    }
  }, [tableId]);

  // Fetch conversation token when dialog opens
  // Always fetch a fresh token to ensure latest agent configuration
  useEffect(() => {
    if (isOpen) {
      console.log('Dialog opened, fetching fresh conversation token...');
      fetchConversationToken();
    }
  }, [isOpen]);

  const fetchConversationToken = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${API}/voice-ai/token`, {
        restaurant_id: restaurantId
      });

      if (response.data) {
        console.log('✅ Token received. Agent ID:', response.data.agent_id);
        setConversationToken(response.data.token);
        setAgentId(response.data.agent_id);
      } else {
        console.error('❌ No data in token response');
      }
    } catch (error) {
      console.error('Error fetching conversation token:', error);
      const errorMsg = error.response?.data?.detail || 'Voice AI is not available for this restaurant.';
      toast.error(errorMsg);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const startConversation = async () => {
    if (!conversationToken) {
      toast.error('Unable to start conversation. Please try again.');
      return;
    }

    if (!agentId) {
      toast.error('Voice AI agent not configured.');
      return;
    }

    try {
      setConversationStatus('connecting');
      
      // Request microphone permission first
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('✅ Microphone access granted');
      } catch (micError) {
        console.error('Microphone access denied:', micError);
        toast.error('Microphone access is required for voice orders. Please allow microphone access and try again.');
        setConversationStatus('ended');
        return;
      }
      
      // Start ElevenLabs conversation session
      console.log('Starting ElevenLabs session with agent:', agentId);
      await conversation.startSession({
        agentId: agentId,
        connectionType: 'webrtc' // Use WebRTC for better audio quality
      });
      console.log('✅ ElevenLabs session started successfully');
      
      setConversationStatus('active');
    } catch (error) {
      console.error('Error starting conversation:', error);
      const errorMessage = error.message || 'Failed to connect to voice system';
      toast.error(errorMessage);
      setConversationStatus('ended');
    }
  };

  const endConversation = async () => {
    try {
      await conversation.endSession();
      setConversationStatus('ended');
      toast.success('Call ended');
    } catch (error) {
      console.error('Error ending conversation:', error);
    }
  };
  
  // Reset state when dialog closes
  const handleDialogClose = () => {
    setIsOpen(false);
    setConversationTranscript([]);
    setOrderProcessed(false);
    setOrderPlaced(false);
    setOrderSummary(null);
    setConversationStatus('idle');
  };

  const handleClose = () => {
    if (conversationStatus === 'active') {
      endConversation();
    }
    setIsOpen(false);
    setConversationStatus('idle');
    setOrderPlaced(false);
    setOrderSummary(null);
    setConversationToken(null);
    setAgentId(null);
  };

  return (
    <>
      {/* Floating Button - Only show when not in call */}
      {conversationStatus === 'idle' && !isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center gap-2"
        >
          <Mic className="h-6 w-6" />
          <span className="font-semibold pr-2">Talk to AI Waiter</span>
        </button>
      )}

      {/* Initial Dialog - Only for starting */}
      <Dialog open={isOpen && conversationStatus === 'idle'} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Volume2 className="h-5 w-5 text-blue-600" />
              AI Voice Waiter
            </DialogTitle>
            <DialogDescription>
              {conversationStatus === 'idle' && 'Click the button below to start your voice order'}
              {conversationStatus === 'connecting' && 'Connecting to AI waiter...'}
              {conversationStatus === 'active' && 'Speak clearly to place your order'}
              {conversationStatus === 'ended' && orderPlaced && 'Order placed successfully!'}
              {conversationStatus === 'ended' && !orderPlaced && 'Conversation ended'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center justify-center py-8 space-y-6">
            {/* Status Indicator */}
            <div className="relative">
              {conversationStatus === 'active' && (
                <div className="absolute inset-0 animate-ping">
                  <div className="h-32 w-32 rounded-full bg-blue-400 opacity-75"></div>
                </div>
              )}
              <div className={`h-32 w-32 rounded-full flex items-center justify-center ${
                conversationStatus === 'active' ? 'bg-blue-500' :
                conversationStatus === 'connecting' ? 'bg-yellow-500' :
                conversationStatus === 'ended' && orderPlaced ? 'bg-green-500' :
                'bg-gray-300'
              }`}>
                {conversationStatus === 'idle' && <Mic className="h-16 w-16 text-white" />}
                {conversationStatus === 'connecting' && <Loader2 className="h-16 w-16 text-white animate-spin" />}
                {conversationStatus === 'active' && <Volume2 className="h-16 w-16 text-white" />}
                {conversationStatus === 'ended' && orderPlaced && <CheckCircle2 className="h-16 w-16 text-white" />}
                {conversationStatus === 'ended' && !orderPlaced && <PhoneOff className="h-16 w-16 text-white" />}
              </div>
            </div>

            {/* Status Text & Order Summary */}
            <div className="text-center w-full">
              {conversationStatus === 'idle' && (
                <p className="text-gray-600">Ready to take your order</p>
              )}
              {conversationStatus === 'connecting' && (
                <p className="text-gray-600">Connecting to AI waiter...</p>
              )}
              {conversationStatus === 'active' && (
                <>
                  <p className="text-lg font-semibold text-gray-800">Listening...</p>
                  <p className="text-sm text-gray-500 mt-1">Speak your order clearly</p>
                </>
              )}
              {conversationStatus === 'ended' && orderPlaced && orderSummary && (
                <div className="w-full">
                  <p className="text-lg font-semibold text-green-600 mb-4">Order Placed Successfully!</p>
                  
                  {/* Order Summary Card */}
                  <div className="bg-gray-50 rounded-lg p-4 text-left border border-gray-200">
                    <h3 className="font-semibold text-gray-800 mb-3 text-center">Order Summary</h3>
                    
                    {/* Customer Info */}
                    <div className="mb-3 pb-3 border-b border-gray-300">
                      <p className="text-sm text-gray-600">
                        <strong>Name:</strong> {orderSummary.customerName}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Phone:</strong> {orderSummary.customerPhone}
                      </p>
                      {orderSummary.orderId && (
                        <p className="text-xs text-gray-500 mt-1">
                          Order ID: {orderSummary.orderId.slice(0, 8)}...
                        </p>
                      )}
                    </div>
                    
                    {/* Items List */}
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Items:</p>
                      {orderSummary.items && orderSummary.items.length > 0 ? (
                        orderSummary.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>{item.quantity}x {item.item_name || item.name}</span>
                            <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 italic">No items found</p>
                      )}
                    </div>
                    
                    {/* Special Instructions */}
                    {orderSummary.specialInstructions && (
                      <div className="mb-3 pb-3 border-b border-gray-300">
                        <p className="text-xs text-gray-500">
                          <strong>Notes:</strong> {orderSummary.specialInstructions}
                        </p>
                      </div>
                    )}
                    
                    {/* Total */}
                    <div className="flex justify-between font-bold text-gray-800 text-lg pt-2 border-t border-gray-300">
                      <span>Total:</span>
                      <span className="text-green-600">${orderSummary.total ? orderSummary.total.toFixed(2) : '0.00'}</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-4">Your order has been sent to the kitchen!</p>
                </div>
              )}
              {conversationStatus === 'ended' && !orderPlaced && (
                <p className="text-gray-600">Conversation ended</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              {conversationStatus === 'idle' && !isLoading && (
                <Button
                  onClick={startConversation}
                  className="bg-blue-600 hover:bg-blue-700"
                  size="lg"
                >
                  <Mic className="h-5 w-5 mr-2" />
                  Start Voice Order
                </Button>
              )}
              
              {conversationStatus === 'active' && (
                <Button
                  onClick={endConversation}
                  variant="destructive"
                  size="lg"
                >
                  <PhoneOff className="h-5 w-5 mr-2" />
                  End Call
                </Button>
              )}

              {(conversationStatus === 'ended' || isLoading) && (
                <Button
                  onClick={handleClose}
                  variant="outline"
                  size="lg"
                >
                  Close
                </Button>
              )}
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading...</span>
              </div>
            )}
          </div>

          {/* Info Footer */}
          {conversationStatus === 'idle' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
              <p className="text-xs text-blue-800 mb-2">
                <strong>How it works:</strong> Click "Start Voice Order" and speak with our AI waiter. 
                They'll help you browse the menu, answer questions, and place your order.
              </p>
              <p className="text-xs text-blue-600">
                <strong>Note:</strong> You'll be asked to allow microphone access. This is required for voice ordering.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating Mini Call UI - Shows during active call */}
      {(conversationStatus === 'connecting' || conversationStatus === 'active') && (
        <div className="fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl w-80 border-2 border-blue-500 animate-slide-up">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Volume2 className="h-5 w-5" />
              <span className="font-semibold text-sm">AI Voice Waiter</span>
            </div>
            <button
              onClick={endConversation}
              className="hover:bg-white/20 rounded-full p-1 transition"
            >
              <PhoneOff className="h-4 w-4" />
            </button>
          </div>

          {/* Status Content */}
          <div className="p-4 bg-white rounded-b-2xl">
            {/* Animated Microphone */}
            <div className="flex flex-col items-center mb-3">
              <div className={`relative ${conversationStatus === 'active' ? 'animate-pulse' : ''}`}>
                {conversationStatus === 'active' && (
                  <div className="absolute inset-0 animate-ping">
                    <div className="h-16 w-16 rounded-full bg-blue-400 opacity-75"></div>
                  </div>
                )}
                <div className={`h-16 w-16 rounded-full flex items-center justify-center ${
                  conversationStatus === 'active' ? 'bg-blue-500' : 'bg-yellow-500'
                }`}>
                  {conversationStatus === 'connecting' && <Loader2 className="h-8 w-8 text-white animate-spin" />}
                  {conversationStatus === 'active' && <Mic className="h-8 w-8 text-white" />}
                </div>
              </div>
            </div>

            {/* Status Text */}
            <div className="text-center">
              {conversationStatus === 'connecting' && (
                <p className="text-sm text-gray-600">Connecting...</p>
              )}
              {conversationStatus === 'active' && (
                <>
                  <p className="text-sm font-semibold text-gray-800">Listening...</p>
                  <p className="text-xs text-gray-500 mt-1">Speak your order</p>
                </>
              )}
            </div>

            {/* Instruction */}
            <div className="mt-3 p-2 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-800 text-center">
                Browse menu below while talking
              </p>
            </div>

            {/* End Call Button */}
            <button
              onClick={endConversation}
              className="w-full mt-3 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2"
            >
              <PhoneOff className="h-4 w-4" />
              End Call
            </button>
          </div>
        </div>
      )}

      {/* Order Summary Floating Window - Shows after order placed */}
      {conversationStatus === 'ended' && orderPlaced && orderSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-bounce-in">
            {/* Success Icon */}
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-green-500 flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-white" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center text-green-600 mb-4">
              Order Placed Successfully!
            </h2>
            
            {/* Order Summary Card */}
            <div className="bg-gray-50 rounded-lg p-4 text-left border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-3 text-center">Order Summary</h3>
              
              {/* Customer Info */}
              <div className="mb-3 pb-3 border-b border-gray-300">
                <p className="text-sm text-gray-600">
                  <strong>Name:</strong> {orderSummary.customerName}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Phone:</strong> {orderSummary.customerPhone}
                </p>
                {orderSummary.orderId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Order ID: {orderSummary.orderId.slice(0, 8)}...
                  </p>
                )}
              </div>
              
              {/* Items List */}
              <div className="mb-3">
                <p className="text-sm font-semibold text-gray-700 mb-2">Items:</p>
                {orderSummary.items && orderSummary.items.length > 0 ? (
                  orderSummary.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>{item.quantity}x {item.item_name || item.name}</span>
                      <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">No items found</p>
                )}
              </div>
              
              {/* Special Instructions */}
              {orderSummary.specialInstructions && (
                <div className="mb-3 pb-3 border-b border-gray-300">
                  <p className="text-xs text-gray-500">
                    <strong>Notes:</strong> {orderSummary.specialInstructions}
                  </p>
                </div>
              )}
              
              {/* Total */}
              <div className="flex justify-between font-bold text-gray-800 text-lg pt-2 border-t border-gray-300">
                <span>Total:</span>
                <span className="text-green-600">${orderSummary.total.toFixed(2)}</span>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 mt-4 text-center">Your order has been sent to the kitchen!</p>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-4">
              {/* Add More Items Button */}
              <button
                onClick={() => {
                  handleClose();
                  if (onOrderComplete) {
                    onOrderComplete({ action: 'continue_browsing' });
                  }
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold transition"
              >
                Add More Items
              </button>
              
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-bold transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
